import {memo, useEffect, useState, useCallback} from 'react'
import {HelpIcon, SecondaryTrashIcon} from '../../icons'
import text from './text.json'
import data from '../../data/conditionTypes.json'
import Tooltip from '../../components/core/tooltip'
import {colors} from '../../utils/colors'
import Input from '../../components/core/input'
import classNames from 'classnames'
import TextHelper from '../../utils/text'

const MESSAGE_FIELD = 'message'

const FilterFields = ({logsFields, isDarkMode = false, fieldPairs, setFieldsPairs}) => {
    const [focusedText, setFocusedText] = useState('')
    const [focusedIndex, setFocusedIndex] = useState(null)
    const [isNewFieldClicked, setNewFieldClicked] = useState(false)
    const [fieldSuggestions, setFieldSuggestions] = useState([])
    const lastFieldPairIndex = fieldPairs.length - 1

    const updateFocusedText = useCallback(field => setFocusedText(field), [])

    const removeFieldPair = useCallback(index => {
        const updated = fieldPairs.filter((_, i) => i !== index)
        setFieldsPairs(updated)
        setFocusedIndex(updated.length - 1)
    }, [fieldPairs, setFieldsPairs])

    const addEmptyFieldPairIfNeeded = useCallback(() => {
        const lastPair = fieldPairs[lastFieldPairIndex]
        if (lastPair && (lastPair.field_name || lastPair.value)) {
            setFieldsPairs(prev => [
                ...prev,
                {field_name: '', condition: data.log_management_conditions.values.equal, value: ''}
            ])
        }
    }, [fieldPairs, setFieldsPairs])

    const handleFieldChange = (index, value) => {
        setFieldsPairs(prev =>
            prev.map((p, i) => (i === index ? {...p, field_name: value} : p))
        )

        const matches = value.length
            ? logsFields.filter(field => field.toLowerCase().includes(value.toLowerCase()))
            : []
        setFieldSuggestions(matches)

        if (index === lastFieldPairIndex) addEmptyFieldPairIfNeeded()
    }

    const handleValueChange = (index, value) => {
        setFieldsPairs(prev =>
            prev.map((p, i) => (i === index ? {...p, value} : p))
        )
        if (index === lastFieldPairIndex) addEmptyFieldPairIfNeeded()
    }

    useEffect(() => {
        if (focusedIndex !== null) {
            setFieldsPairs(prev =>
                prev.map((pair, idx) =>
                    idx === focusedIndex ? {...pair, field_name: focusedText} : pair
                )
            )
        }
    }, [focusedText])

    useEffect(() => {
        if (isNewFieldClicked && focusedIndex === lastFieldPairIndex) {
            addEmptyFieldPairIfNeeded()
            setNewFieldClicked(false)
        }
        setFocusedIndex(lastFieldPairIndex)
    }, [isNewFieldClicked])

    return (
        <div className='filter-section'>
            <div className='field-builder'>
                <div className={classNames('field-input-area', {
                    dark: isDarkMode
                })}>
                    <div className='field-condition-list'>
                        {fieldPairs.map((pair, index) => (
                            <div className={classNames('field-condition-row', {
                                dark: isDarkMode
                            })}
                            key={index}
                            >
                                <div className='field-row-wrapper'>
                                    <Input
                                        type='text'
                                        value={pair.field_name}
                                        placeholder={text.field}
                                        name='field_name'
                                        onFocus={() => setFocusedIndex(index)}
                                        onChange={e => handleFieldChange(index, e.target.value)}
                                    />
                                    {index === focusedIndex && fieldSuggestions.length > 0 && (
                                        <div className={classNames('field-suggestions', {
                                            dark: isDarkMode
                                        })}>
                                            <h4>{text.suggestions_title}</h4>
                                            <ul>
                                                {fieldSuggestions.map((field, idx) => (
                                                    <li
                                                        key={idx}
                                                        title={field}
                                                        className='suggestion-option'
                                                        onClick={() => {
                                                            setFieldsPairs(prev =>
                                                                prev.map((p, i) => (i === index ? {...p, field_name: field} : p))
                                                            )
                                                            setFieldSuggestions([])
                                                        }}
                                                    >
                                                        {TextHelper.textLengthChecker(field, 30)}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <Input
                                        name='condition'
                                        placeholder={text.condition}
                                        inputIsDisabled={true}
                                        value={pair.field_name === MESSAGE_FIELD ?
                                            data.log_management_conditions.values.contains.label
                                            : pair.condition.label}
                                    />
                                    <Input
                                        type='text'
                                        value={pair.value}
                                        placeholder={text.value}
                                        name='value'
                                        onChange={e => handleValueChange(index, e.target.value)}
                                    />
                                </div>
                                {index === 0 ? (
                                    <Tooltip
                                        placement='left'
                                        trigger='click'
                                        content={<span dangerouslySetInnerHTML={{__html: text.json_info}} />}
                                    >
                                        <HelpIcon stroke={colors.colorBgFilter} width='22' height='22' />
                                    </Tooltip>
                                ) : (
                                    <button
                                        onClick={() => removeFieldPair(index)}
                                        className='field-remove-button'
                                    >
                                        <SecondaryTrashIcon stroke={colors.colorRed} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <div className={classNames('field-suggestion-list', {
                    dark: isDarkMode
                })}>
                    <h3 className={classNames('field-suggestion-header', {
                        dark: isDarkMode
                    })}>
                        {text.available_fields}
                    </h3>
                    {logsFields.map((field, idx) => (
                        <span
                            className={classNames('suggestion-text', {
                                disabled: field?.startsWith('extra.')
                            })}
                            key={idx}
                            title={field}
                            onClick={() => {
                                updateFocusedText(field)
                                setNewFieldClicked(true)
                            }}
                            onMouseEnter={() => {
                                updateFocusedText(field)
                                setNewFieldClicked(false)
                            }}
                            onMouseLeave={() => !isNewFieldClicked && updateFocusedText('')}
                        >
                            {TextHelper.textLengthChecker(field, 32)}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default memo(FilterFields)
